import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Shield } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";
import type { Lang } from "@/i18n/locale";
import { newProfileLanguagePatch } from "@/lib/profileLanguage";
import { analytics, appPlatform, type SignupProvider } from "@/lib/analytics";
import { metaIdentify, metaTrack, sendMetaCapi, signupEventId } from "@/lib/metaPixel";
import { Button } from "@/components/ui/button";
import {
  claimReferral,
  clearStoredReferralCode,
  normalizeReferralCode,
  readStoredReferralCode,
} from "@/lib/referral";

/**
 * OAuth callback landing page (Google / Apple / future providers).
 *
 * Flow:
 *   1. Render loading spinner immediately (premium dark theme).
 *   2. Read session via supabase.auth.getSession(). Supabase auth-helpers
 *      complete the OAuth code exchange under the hood — by the time this
 *      page mounts we should have a session row.
 *   3. Check user_consents for a privacy_policy row.
 *        - missing → render modal blocker (no close affordance, no outside
 *          click dismiss), require checkbox + INSERT, then continue.
 *        - present → proceed to routing.
 *   4. Routing (Direto ao Espelho — corridor is /auth → (consent) → /mirror):
 *        - onboarding_complete === true → /mirror (returning user)
 *        - otherwise (no row, or a row that never reached complete) → create/
 *          repair the minimal gate row (onboarding_complete=true, name/age/sex
 *          NULL) and go straight to /mirror. Identity/Avatar remain as routes
 *          (nudged from the Mirror + Settings), just off the default corridor.
 *        - if that row write fails → fall back to /onboarding/identity (classic
 *          corridor recreates the row) so the gate never loops.
 *
 * Anti-patterns avoided:
 *   - localStorage for consent state (uses React state).
 *   - window.location.href (uses react-router navigate).
 *   - flashing loading <300ms (intentional minimum visual settle).
 */

const PRIVACY_POLICY_VERSION = "v1.0";

// NOTE: A prewarmMirrorEdgeFunctions() helper used to fire 3 invalid POSTs
// to mirror-chat / mirror-stt / mirror-tts here to mitigate cold-start.
// All three returned 400 because the warmup payload didn't match each
// function's schema — Sentry + console noise with zero warm-up benefit
// (Supabase Edge Functions don't measurably pre-warm from a 400 invoke).
// Removed in the Plans V3.2 Phase 1 commit. Cold-start mitigation is now
// the responsibility of natural traffic / reserved instances.

async function hasPrivacyConsent(userId: string): Promise<boolean> {
  const { data, error } = await (supabase as any)
    .from("user_consents")
    .select("id")
    .eq("user_id", userId)
    .eq("consent_type", "privacy_policy")
    .limit(1)
    .maybeSingle();
  if (error) {
    console.error("[AuthCallback] consent lookup failed:", error);
    // Conservative default — assume missing so we re-prompt rather than
    // silently skip on transient errors. Re-prompt is idempotent.
    return false;
  }
  return !!data;
}

// Direto ao Espelho — the minimal user_profiles row the onboarding gate
// (useOnboardingGate) needs to let /mirror through: onboarding_complete=true.
// Identity/Avatar are no longer on the default corridor, so first_name/age/sex
// stay NULL — Apple Guideline 5.1.1(v) (sex+age must not be required), and the
// voice engine already defaults a null sex to Bia (never a wrong-gender voice).
// The sex_valid_when_completed CHECK permits a null sex on a completed row, so
// this shape is DB-valid. avatar_source='none' mirrors the Avatar "skip" shape.
// Idempotent (onConflict user_id) so an onAuthStateChange double-fire is a no-op.
//
// Returns true ONLY when a row is confirmed present. The .select() guard is the
// silent-fail-upsert rule: 0 rows means an RLS block, which must NOT be treated
// as success — otherwise we navigate to a /mirror the gate immediately bounces
// back to /onboarding/identity (the loop this whole flow exists to avoid).
async function ensureMirrorReadyProfile(userId: string, lang: Lang): Promise<boolean> {
  // Este upsert roda em TODO retorno de OAuth, não só no primeiro — por isso o
  // idioma entra pelo helper, que devolve {} quando a linha já existe. Sem
  // essa guarda, cada login sobrescreveria a escolha de idioma do usuário.
  const langPatch = await newProfileLanguagePatch(userId, lang);

  const { data, error } = await supabase
    .from("user_profiles")
    .upsert(
      {
        user_id: userId,
        onboarding_complete: true,
        avatar_source: "none",
        avatar_type: "placeholder",
        ...langPatch,
      } as any,
      { onConflict: "user_id" },
    )
    .select();
  if (error) {
    console.error("[AuthCallback] direct-to-mirror profile upsert failed:", error);
    return false;
  }
  if (!Array.isArray(data) || data.length === 0) {
    console.error("[AuthCallback] direct-to-mirror upsert returned 0 rows — likely RLS block");
    return false;
  }
  return true;
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();

  const [userId, setUserId] = useState<string | null>(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const [routing, setRouting] = useState(false);

  const finalizeRouting = useCallback(async (uid: string) => {
    setRouting(true);

    // One lookup, three outcomes — gated on onboarding_complete itself (NOT mere
    // row existence): a snapshot upsert can leave a row with onboarding_complete
    // != true, and routing such a user to /mirror would loop through the gate.
    const { data: profile, error } = await supabase
      .from("user_profiles")
      .select("onboarding_complete")
      .eq("user_id", uid)
      .maybeSingle();

    if (error) {
      // Transient lookup error — mirror the gate's own philosophy (never bounce a
      // possibly-returning user) and head to /mirror. A truly new user with no row
      // is then routed on by the gate itself to /onboarding/identity.
      console.error("[AuthCallback] finalizeRouting lookup failed:", error);
      navigate("/mirror", { replace: true });
      return;
    }

    // (c) Already onboarded → straight in. No write, no analytics.
    if (profile?.onboarding_complete === true) {
      navigate("/mirror", { replace: true });
      return;
    }

    // (a) Brand-new user (no row at all) → this OAuth login is a completed signup.
    // signup_started fires HERE for social, atomically before signup_completed,
    // instead of on the handleOAuth button click. AuthCallback is the single point
    // that sees 100% of social completions (web + native deep-link both route
    // here), so this closes the coverage gap (redirect race / deep-link / restored
    // session that completed without a started). Interrupted-onboarding rows (b)
    // do NOT re-fire — they already counted on their first login. Email keeps its
    // own form-engagement started in Auth.tsx.
    if (profile === null) {
      const { data: { session } } = await supabase.auth.getSession();
      const provider = (session?.user?.app_metadata as { provider?: string } | undefined)?.provider;
      const prov = (provider as SignupProvider) || "google";
      // ── E-MAIL NÃO RECONTA AQUI ────────────────────────────────────────────
      // Até o toggle "Confirm email" existir, um cadastro por e-mail NUNCA
      // passava por este callback: ele ia direto do /auth para o onboarding.
      // Com a confirmação ligada ele passa — e passa com `profile === null`,
      // que é a condição deste bloco. Sem esta guarda, `signup_completed`
      // sairia DUAS vezes para o mesmo cadastro (uma no Auth.tsx, no instante
      // em que a linha em auth.users nasce, outra aqui) e o funil de e-mail
      // dobraria em silêncio, do dia do deploy em diante.
      //
      // O par do Meta já era imune por outro motivo (o event_id é
      // determinístico, `signup-<user_id>`, então lá o servidor dedupa) — o
      // que NÃO protege a contagem do PostHog.
      const isEmailProvider = prov === "email";
      if (!isEmailProvider) {
        analytics.signupStarted(prov);
        analytics.signupCompleted(prov);
      } else {
        // O FECHO do funil de confirmação (task 1b643867): e-mail chegando
        // aqui com `profile === null` é a PRIMEIRA sessão da conta — com o
        // toggle ligado, isso só existe DEPOIS de confirmar o e-mail.
        // Recovery de senha não passa por este bloco (quem recupera senha já
        // tem profile). Não re-conta signup_completed (a guarda acima segue);
        // conta a CONFIRMAÇÃO, por plataforma — o denominador do "reavaliar
        // se a perda passar de 30%".
        analytics.signupEmailConfirmed(appPlatform());
      }
      // Meta mirror (browser + CAPI) + Advanced Matching. Session email is
      // available here for 100% of social completions. The event_id is
      // DETERMINISTIC (signup-<user_id>) so the SERVER event emitted from
      // the auth.users row by meta-capi-signup-dispatch dedupes against the
      // browser paths — the server pair is what fires for the ~55% of
      // webview OAuth round-trips that never reach this callback at all.
      const metaEmail = session?.user?.email ?? null;
      const metaUserId = session?.user?.id;
      const metaEventId = metaTrack(
        "CompleteRegistration",
        { method: prov },
        metaUserId ? signupEventId(metaUserId) : undefined,
      );
      void metaIdentify(metaEmail);
      void sendMetaCapi("CompleteRegistration", metaEventId, { method: prov }, metaEmail);

      // ── Indicação do cadastro por E-MAIL, resgatada aqui ───────────────────
      // `claimReferral` precisa de sessão, e no cadastro com confirmação ligada
      // não há sessão nenhuma no /auth (Auth.tsx pula a chamada por isso). O
      // código fica guardado — só é limpo em "ok" — e este é o primeiro
      // instante com sessão. Sem isto, todo indicado que se cadastra por
      // e-mail sumiria calado no dia em que o toggle ligasse.
      //
      // SÓ e-mail: Google e Apple seguem exatamente como hoje, sem resgate
      // (lacuna antiga, fora deste escopo — mexer nela mudaria o comportamento
      // de social, que a decisão travada manda não tocar).
      if (isEmailProvider) {
        const refCode = normalizeReferralCode(readStoredReferralCode() ?? "");
        if (refCode) {
          // Best-effort e SILENCIOSO: no /auth os erros viram toast porque a
          // pessoa está num formulário e pode corrigir; aqui ela está no meio
          // de uma navegação e não há o que corrigir.
          const result = await claimReferral(refCode);
          if (result === "ok") clearStoredReferralCode();
        }
      }
    }

    // Direto ao Espelho — create/repair the gate row, then go straight to the
    // Mirror. On failure fall back to the classic corridor (Processing recreates
    // the row) rather than navigating to a /mirror the gate would bounce = loop.
    const ready = await ensureMirrorReadyProfile(uid, lang);
    navigate(ready ? "/mirror" : "/onboarding/identity", { replace: true });
  }, [navigate, lang]);

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();
    // One-shot guard — proceed() and handleError() each call navigate(), so
    // both the onAuthStateChange path AND the parallel getSession() path
    // race to deliver the session. The first to win wins; subsequent
    // attempts are ignored.
    const processed = { current: false };
    let timeoutHandle: number | undefined;

    const proceed = async (uid: string) => {
      if (cancelled || processed.current) return;
      processed.current = true;
      if (timeoutHandle !== undefined) {
        clearTimeout(timeoutHandle);
        timeoutHandle = undefined;
      }
      setUserId(uid);

      const consented = await hasPrivacyConsent(uid);
      if (cancelled) return;

      // Minimum visual settle so the loading state never flashes
      // sub-perceptually (jarring on fast networks). 300ms felt right in
      // similar VYV transitions (Splash → Mirror handoff 30/04).
      const elapsed = Date.now() - startedAt;
      const minDelay = 300;
      if (elapsed < minDelay) {
        await new Promise((res) => setTimeout(res, minDelay - elapsed));
      }
      if (cancelled) return;

      if (consented) {
        await finalizeRouting(uid);
      } else {
        setShowConsentModal(true);
      }
    };

    const handleError = (msg: string) => {
      if (cancelled || processed.current) return;
      processed.current = true;
      if (timeoutHandle !== undefined) {
        clearTimeout(timeoutHandle);
        timeoutHandle = undefined;
      }
      console.warn("[AuthCallback] aborting to /auth:", msg);
      toast.error(msg);
      navigate("/auth", { replace: true });
    };

    // ── Step 0: surface explicit OAuth provider errors from the redirect URL ──
    // Providers can return `?error=access_denied&error_description=...` or
    // (implicit flow) the same params after `#`. Either way, show the
    // human-readable reason instead of waiting for a generic timeout.
    if (typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search);
      const searchErr =
        searchParams.get("error_description") ?? searchParams.get("error");
      if (searchErr) {
        handleError(searchErr);
        return () => { cancelled = true; };
      }
      const rawHash = window.location.hash.startsWith("#")
        ? window.location.hash.slice(1)
        : window.location.hash;
      if (rawHash) {
        const hashParams = new URLSearchParams(rawHash);
        const hashErr =
          hashParams.get("error_description") ?? hashParams.get("error");
        if (hashErr) {
          handleError(hashErr);
          return () => { cancelled = true; };
        }
      }
    }

    // ── Step 1: subscribe BEFORE checking the session ──
    // The OAuth URL parsing inside auth-js _initialize() schedules a
    // setTimeout(0) before emitting SIGNED_IN (GoTrueClient.js:300). If we
    // call getSession() first and then attach the listener, we can miss
    // that event entirely. Subscribing first guarantees we catch SIGNED_IN
    // / INITIAL_SESSION / TOKEN_REFRESHED whenever auth-js fires them.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (cancelled || processed.current) return;
        const uid = session?.user?.id;
        if (!uid) return;
        if (
          event === "SIGNED_IN" ||
          event === "INITIAL_SESSION" ||
          event === "TOKEN_REFRESHED"
        ) {
          void proceed(uid);
        }
      },
    );

    // ── Step 2: parallel tiebreaker — already-cached session path ──
    // For returning users whose session was already in localStorage before
    // they hit /auth/callback (e.g. an OAuth re-login in the same browser).
    // getSession() awaits the auth-js initializePromise so it also sees the
    // URL fragment parsing result, but won't necessarily reach us before
    // the SIGNED_IN event.
    void (async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (cancelled || processed.current) return;
        if (error) {
          handleError(error.message);
          return;
        }
        const uid = data.session?.user?.id;
        if (uid) {
          void proceed(uid);
        }
        // else: no session in storage yet — wait for onAuthStateChange.
      } catch (err: any) {
        console.error("[AuthCallback] getSession threw:", err);
        handleError(err?.message ?? t("auth.callback.errorTitle"));
      }
    })();

    // ── Step 3: hard timeout ──
    // If neither path produces a session within 5 s the OAuth handshake
    // very likely failed silently (provider config drift, PKCE/implicit
    // mismatch, network gap during code exchange). Surface a generic
    // error and bounce back to /auth so the user can retry.
    timeoutHandle = window.setTimeout(() => {
      if (cancelled || processed.current) return;
      console.warn("[AuthCallback] OAuth handshake timed out after 5s");
      handleError(t("auth.callback.errorTitle"));
    }, 5000);

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    };
  }, [finalizeRouting, navigate, t]);

  const handleAcceptConsent = useCallback(async () => {
    if (!userId || !consentAccepted) return;
    setConsentSubmitting(true);
    try {
      const { error } = await (supabase as any)
        .from("user_consents")
        .insert({
          user_id: userId,
          consent_type: "privacy_policy",
          policy_version: PRIVACY_POLICY_VERSION,
          ip_hash: null,
          user_agent: navigator.userAgent.slice(0, 500),
        });
      if (error) {
        // Surface, keep modal open for retry (do not unmount on failure).
        console.error("[CONSENT_LOG_FAILED]", error);
        toast.error(error.message ?? t("auth.callback.errorTitle"));
        setConsentSubmitting(false);
        return;
      }
      toast.success(t("auth.consent.acceptAndContinue"));
      setShowConsentModal(false);
      await finalizeRouting(userId);
    } catch (err: any) {
      console.error("[CONSENT_LOG_FAILED]", err);
      toast.error(err?.message ?? t("auth.callback.errorTitle"));
      setConsentSubmitting(false);
    }
  }, [userId, consentAccepted, finalizeRouting, t]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ background: "linear-gradient(135deg, #050811 0%, #0a0e1a 50%, #050811 100%)" }}
    >
      {/* Loading state — always visible until we navigate away or modal opens. */}
      {!showConsentModal && (
        <div className="flex flex-col items-center gap-4 text-white/80">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-300/80" />
          <p className="text-sm font-display tracking-wide">
            {routing ? t("common.loading") : t("auth.callback.loading")}
          </p>
        </div>
      )}

      {/* Consent modal blocker — no close affordance, outside click ignored. */}
      {showConsentModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-5"
          style={{ background: "rgba(0,0,0,0.85)" }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-2xl p-6 ring-1 ring-white/10"
            style={{ background: "linear-gradient(135deg, #0a0e1a 0%, #0f1419 50%, #0a0e1a 100%)" }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="oauth-consent-title"
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: "rgba(34,211,238,0.10)", border: "0.5px solid rgba(34,211,238,0.30)" }}
              >
                <Shield size={18} className="text-cyan-300" />
              </div>
              <h2
                id="oauth-consent-title"
                className="font-display text-lg font-semibold text-foreground"
              >
                {t("auth.consent.modalTitle")}
              </h2>
            </div>

            <p className="text-muted-foreground text-sm leading-relaxed mb-5">
              {t("auth.consent.modalBody")}
            </p>

            <label
              htmlFor="oauth-consent-checkbox"
              className="flex items-start gap-2.5 mb-5 cursor-pointer select-none"
            >
              <input
                id="oauth-consent-checkbox"
                type="checkbox"
                checked={consentAccepted}
                onChange={(e) => setConsentAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-border/60 bg-secondary/60 accent-primary cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              <span className="text-muted-foreground text-[12px] leading-relaxed">
                {t("auth.consent.checkboxLabelPrefix")}
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-primary hover:underline focus:outline-none focus:underline"
                >
                  {t("auth.consent.privacyPolicy")}
                </Link>
                {t("auth.consent.andConnector")}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-primary hover:underline focus:outline-none focus:underline"
                >
                  {t("auth.consent.termsOfService")}
                </Link>
              </span>
            </label>

            <Button
              variant="hero"
              size="xl"
              className={`w-full ${(!consentAccepted || consentSubmitting) ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={handleAcceptConsent}
              disabled={!consentAccepted || consentSubmitting}
              aria-disabled={!consentAccepted || consentSubmitting}
            >
              {consentSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("auth.consent.acceptAndContinue")
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
